import React, { useEffect, useMemo, useState } from "react";
import { API } from "../lib/api";
import { toast } from "sonner";
import { Lock, LogOut, MessageSquare, Wind, Heart, CloudRain, Users, Download, ChevronRight, Search, KeyRound, X, Star, Eye, EyeOff } from "lucide-react";

const STORAGE_KEY = "coulisses_token";
const MOOD_EMOJIS = ["😞", "😐", "🙂", "😊", "🤩"];

function LoginCard({ onSuccess }) {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!pwd.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/verify`, { headers: { "X-Admin-Token": encodeURIComponent(pwd) } });
      if (res.ok) {
        localStorage.setItem(STORAGE_KEY, pwd);
        onSuccess(pwd);
      } else {
        toast.error("Mot de passe incorrect");
      }
    } catch {
      toast.error("Impossible de se connecter au serveur");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[60vh] grid place-items-center" data-testid="coulisses-login">
      <form onSubmit={submit} className="bg-white border border-[#E8E6E1] rounded-3xl p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.06)] max-w-md w-full">
        <div className="w-14 h-14 mb-5 rounded-2xl bg-[#3D405B] text-white grid place-items-center">
          <Lock className="w-6 h-6" />
        </div>
        <h1 className="font-heading text-2xl md:text-3xl text-[#3D405B]">Les coulisses</h1>
        <p className="text-sm text-[#8A8D91] mt-2 mb-6 leading-relaxed">
          Espace privé — mot de passe requis. Tu y verras les échanges du Gang, les soucis déposés et les victoires partagées.
        </p>
        <input
          type="password"
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder="Mot de passe"
          data-testid="coulisses-password"
          autoFocus
          className="w-full bg-[#FDFBF7] border border-[#E8E6E1] rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#81B29A]/50 focus:border-[#81B29A] text-[#3D405B]"
        />
        <button
          type="submit"
          disabled={busy || !pwd.trim()}
          data-testid="coulisses-submit"
          className="mt-4 w-full bg-[#E07A5F] text-white rounded-full px-6 py-3 font-medium hover:bg-[#D56D52] transition-colors disabled:opacity-50"
        >
          {busy ? "Vérification…" : "Entrer dans les coulisses"}
        </button>
      </form>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone = "sand" }) {
  const tones = {
    sand:  "bg-[#FDFBF7] border-[#E8E6E1]",
    sage:  "bg-[#81B29A]/10 border-[#81B29A]/30",
    peach: "bg-[#E07A5F]/10 border-[#E07A5F]/30",
    lav:   "bg-[#B08BBB]/10 border-[#B08BBB]/30",
  };
  return (
    <div className={`rounded-2xl border ${tones[tone]} p-4 flex items-center gap-3`}>
      <div className="shrink-0 w-10 h-10 rounded-xl bg-white/70 border border-white grid place-items-center text-[#3D405B]">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[#8A8D91] font-semibold">{label}</div>
        <div className="text-2xl font-heading text-[#3D405B] leading-tight">{value}</div>
      </div>
    </div>
  );
}

function SessionAccordion({ s, index, query }) {
  const [open, setOpen] = useState(index === 0);
  const dt = new Date(s.last_at);
  const dateStr = dt.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  const preview = (s.messages.find((m) => m.role === "user")?.content || "").slice(0, 120);

  const q = query.trim().toLowerCase();
  const highlight = (text) => {
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q);
    if (idx < 0) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="bg-[#F2CC8F]/60 text-[#3D405B] rounded px-0.5">{text.slice(idx, idx + q.length)}</mark>
        {text.slice(idx + q.length)}
      </>
    );
  };

  return (
    <div className="bg-white border border-[#E8E6E1] rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-4 md:p-5 text-left hover:bg-[#FDFBF7] transition-colors"
      >
        <div className="w-9 h-9 shrink-0 rounded-xl bg-[#3D405B] text-white grid place-items-center text-sm font-heading">
          {index + 1}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-xs text-[#8A8D91]">
            <span className="uppercase tracking-widest font-semibold">{dateStr}</span>
            <span>·</span>
            <span>{s.count} message{s.count > 1 ? "s" : ""}</span>
          </div>
          <div className="text-sm text-[#3D405B] mt-1 line-clamp-1 md:line-clamp-2">{highlight(preview) || "(sans texte)"}</div>
        </div>
        <ChevronRight className={`w-5 h-5 text-[#8A8D91] transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-[#E8E6E1] p-4 md:p-5 space-y-3 bg-[#FDFBF7]/60">
          {s.messages.map((m, i) => (
            <div key={i} className={`rounded-2xl p-3 md:p-4 ${m.role === "user" ? "bg-[#3D405B] text-white ml-6 md:ml-16" : "bg-white border border-[#E8E6E1] mr-6 md:mr-16"}`}>
              <div className={`text-[10px] uppercase tracking-widest mb-1 font-semibold ${m.role === "user" ? "text-white/60" : "text-[#E07A5F]"}`}>
                {m.role === "user" ? "Parent" : "Le Gang"}
              </div>
              <div className={`text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "text-white" : "text-[#3D405B]"}`}>
                {highlight(m.content)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function exportCsv(data) {
  const rows = [["type", "date", "session_id", "role", "content"]];
  data.sessions.forEach((s) => {
    s.messages.forEach((m) => rows.push(["chat", m.at, s.session_id, m.role, m.content]));
  });
  data.worries.forEach((w) => rows.push(["worry", w.created_at, "", "", w.content]));
  data.wins.forEach((w) => rows.push(["win", w.created_at, "", "", w.content]));
  data.moods.forEach((m) => rows.push([
    "mood", m.created_at, "", "",
    `parent=${m.parent_mood}, child=${m.child_mood}, emotions=[${(m.emotions || []).join("|")}], note=${(m.note || "").replace(/\n/g, " ")}`,
  ]));
  (data.reviews || []).forEach((r) => rows.push([
    "review", r.created_at, "", "",
    `rating=${r.rating}★ name=${r.name || "(anon)"} lang=${r.lang || "?"} text=${(r.text || "").replace(/\n/g, " ")}`,
  ]));
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `coulisses-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function ChangePasswordModal({ token, onClose, onChanged }) {
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("Mot de passe trop court (8 caractères minimum)");
      return;
    }
    if (next !== confirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Token": encodeURIComponent(token) },
        body: JSON.stringify({ new_password: next }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Erreur");
      }
      toast.success("Mot de passe mis à jour ");
      onChanged(next);
    } catch (e) {
      toast.error(e.message || "Impossible de changer le mot de passe");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-[#3D405B]/40 backdrop-blur-sm p-4" data-testid="change-password-modal">
      <div className="bg-white rounded-3xl border border-[#E8E6E1] shadow-2xl w-full max-w-md p-6 md:p-8 relative">
        <button onClick={onClose} data-testid="change-password-close" className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-[#FDFBF7] grid place-items-center text-[#8A8D91]">
          <X className="w-4 h-4" />
        </button>
        <div className="w-12 h-12 mb-4 rounded-2xl bg-[#3D405B] text-white grid place-items-center">
          <KeyRound className="w-5 h-5" />
        </div>
        <h2 className="font-heading text-2xl text-[#3D405B]">Changer le mot de passe</h2>
        <p className="text-sm text-[#8A8D91] mt-1 mb-5">Il sera stocké en toute sécurité (hash SHA-256) — impossible à retrouver en clair.</p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-[#3D405B] uppercase tracking-widest">Nouveau mot de passe</label>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="8 caractères minimum"
              data-testid="change-password-new"
              autoFocus
              className="mt-1 w-full bg-[#FDFBF7] border border-[#E8E6E1] rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#81B29A]/50 focus:border-[#81B29A] text-[#3D405B]"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#3D405B] uppercase tracking-widest">Confirmer</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="retape-le"
              data-testid="change-password-confirm"
              className="mt-1 w-full bg-[#FDFBF7] border border-[#E8E6E1] rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#81B29A]/50 focus:border-[#81B29A] text-[#3D405B]"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !next || !confirm}
            data-testid="change-password-submit"
            className="w-full bg-[#E07A5F] text-white rounded-full px-6 py-3 font-medium hover:bg-[#D56D52] transition-colors disabled:opacity-50"
          >
            {busy ? "Mise à jour…" : "Enregistrer"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Coulisses() {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("chat");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);

  const load = async (t = token) => {
    if (!t) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/coulisses`, { headers: { "X-Admin-Token": encodeURIComponent(t) } });
      if (res.status === 401) {
        localStorage.removeItem(STORAGE_KEY);
        setToken("");
        toast.error("Session expirée — reconnecte-toi");
        return;
      }
      if (!res.ok) {
        let detail = "";
        try { const j = await res.json(); detail = j.detail || ""; } catch { /* not JSON */ }
        toast.error(`Erreur de chargement (HTTP ${res.status}${detail ? " · " + detail : ""})`);
        return;
      }
      const j = await res.json();
      setData(j);
    } catch (err) {
      toast.error(`Erreur de connexion : ${err.message || "réseau indisponible"}`);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(token); }, [token]);

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken("");
    setData(null);
  };

  const filtered = useMemo(() => {
    if (!data) return null;
    const q = query.trim().toLowerCase();
    if (!q) return data;
    const inText = (t) => (t || "").toLowerCase().includes(q);
    return {
      ...data,
      sessions: data.sessions.filter((s) => s.messages.some((m) => inText(m.content))),
      worries: data.worries.filter((w) => inText(w.content)),
      wins: data.wins.filter((w) => inText(w.content)),
      moods: data.moods.filter((m) => inText(m.note)),
      reviews: (data.reviews || []).filter((r) => inText(r.text) || inText(r.name)),
    };
  }, [data, query]);

  if (!token) return <LoginCard onSuccess={setToken} />;
  if (!data) {
    return <div className="min-h-[40vh] grid place-items-center text-[#8A8D91]">{loading ? "Chargement des coulisses…" : "…"}</div>;
  }

  const T = filtered.totals;

  return (
    <div className="space-y-8" data-testid="coulisses-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#E07A5F] font-semibold">Espace privé</div>
          <h1 className="font-heading text-4xl md:text-5xl text-[#3D405B]">Les coulisses</h1>
          <p className="text-[#8A8D91] mt-2 max-w-2xl">Ce que ta communauté te confie — messages, soucis, victoires, humeurs.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportCsv(data)} className="inline-flex items-center gap-2 rounded-full border border-[#3D405B]/20 bg-white text-[#3D405B] px-4 py-2 text-sm font-medium hover:bg-[#3D405B]/5" data-testid="coulisses-export">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button onClick={() => setShowChangePwd(true)} className="inline-flex items-center gap-2 rounded-full border border-[#3D405B]/20 bg-white text-[#3D405B] px-4 py-2 text-sm font-medium hover:bg-[#3D405B]/5" data-testid="coulisses-change-pwd">
            <KeyRound className="w-4 h-4" /> Mot de passe
          </button>
          <button onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-[#3D405B]/20 bg-white text-[#3D405B] px-4 py-2 text-sm font-medium hover:bg-[#3D405B]/5" data-testid="coulisses-logout">
            <LogOut className="w-4 h-4" /> Sortir
          </button>
        </div>
      </header>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Stat icon={Users} label="Sessions chat" value={T.sessions} tone="peach" />
        <Stat icon={MessageSquare} label="Messages" value={T.chat_messages} tone="peach" />
        <Stat icon={Wind} label="Soucis" value={T.worries} tone="lav" />
        <Stat icon={Heart} label="Victoires" value={T.wins} tone="sage" />
        <Stat icon={CloudRain} label="Humeurs" value={T.moods} tone="sand" />
        <Stat icon={Heart} label="Waitlist" value={T.waitlist} tone="sand" />
        <Stat icon={Star} label="Avis" value={T.reviews || 0} tone="peach" />
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 text-[#8A8D91] absolute left-4 top-1/2 -translate-y-1/2" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher dans les messages, soucis, victoires…"
          data-testid="coulisses-search"
          className="w-full bg-white border border-[#E8E6E1] rounded-full pl-11 pr-5 py-3 focus:outline-none focus:ring-2 focus:ring-[#81B29A]/50 focus:border-[#81B29A] text-[#3D405B]"
        />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: "chat", label: `Chat (${filtered.sessions.length})` },
          { key: "worries", label: `Soucis (${filtered.worries.length})` },
          { key: "wins", label: `Victoires (${filtered.wins.length})` },
          { key: "moods", label: `Humeurs (${filtered.moods.length})` },
          { key: "waitlist", label: `Waitlist (${filtered.waitlist.length})` },
          { key: "reviews", label: `Avis (${(filtered.reviews || []).length})` },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`coulisses-tab-${t.key}`}
            className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
              tab === t.key ? "bg-[#3D405B] text-white border-[#3D405B]" : "bg-white text-[#3D405B] border-[#E8E6E1] hover:border-[#3D405B]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "chat" && (
        <section className="space-y-3" data-testid="tab-chat">
          {filtered.sessions.length === 0 ? (
            <p className="text-[#8A8D91] italic">Aucune conversation pour l&apos;instant.</p>
          ) : (
            filtered.sessions.map((s, i) => <SessionAccordion key={s.session_id} s={s} index={i} query={query} />)
          )}
        </section>
      )}

      {tab === "worries" && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tab-worries">
          {filtered.worries.length === 0 ? (
            <p className="text-[#8A8D91] italic col-span-full">Aucun souci déposé.</p>
          ) : filtered.worries.map((w, i) => (
            <article key={i} className="bg-white border border-[#E8E6E1] rounded-2xl p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#8A8D91]">{new Date(w.created_at).toLocaleString("fr-FR")}</div>
              <p className="text-sm text-[#3D405B] mt-2 leading-relaxed whitespace-pre-wrap">{w.content}</p>
            </article>
          ))}
        </section>
      )}

      {tab === "wins" && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tab-wins">
          {filtered.wins.length === 0 ? (
            <p className="text-[#8A8D91] italic col-span-full">Aucune victoire partagée.</p>
          ) : filtered.wins.map((w, i) => (
            <article key={i} className="bg-[#81B29A]/10 border border-[#81B29A]/30 rounded-2xl p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#8A8D91]">{new Date(w.created_at).toLocaleString("fr-FR")} · {w.lang || "?"}</div>
              <p className="text-sm text-[#3D405B] mt-2 leading-relaxed whitespace-pre-wrap">{w.content}</p>
            </article>
          ))}
        </section>
      )}

      {tab === "moods" && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tab-moods">
          {filtered.moods.length === 0 ? (
            <p className="text-[#8A8D91] italic col-span-full">Aucune humeur enregistrée.</p>
          ) : filtered.moods.map((m, i) => (
            <article key={i} className="bg-white border border-[#E8E6E1] rounded-2xl p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#8A8D91]">{new Date(m.created_at).toLocaleString("fr-FR")}</div>
              <div className="flex items-center gap-4 mt-2">
                <div className="text-center"><div className="text-3xl">{MOOD_EMOJIS[m.parent_mood - 1]}</div><div className="text-[10px] text-[#8A8D91] uppercase">Parent</div></div>
                <div className="text-center"><div className="text-3xl">{MOOD_EMOJIS[m.child_mood - 1]}</div><div className="text-[10px] text-[#8A8D91] uppercase">Enfant</div></div>
                {m.note && <p className="text-sm text-[#3D405B]/85 flex-1 leading-relaxed">{m.note}</p>}
              </div>
              {m.emotions && m.emotions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {m.emotions.map((e) => <span key={e} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#F4F1EA] text-[#3D405B] border border-[#E8E6E1]">{e}</span>)}
                </div>
              )}
            </article>
          ))}
        </section>
      )}

      {tab === "waitlist" && (
        <section className="space-y-2" data-testid="tab-waitlist">
          {filtered.waitlist.length === 0 ? (
            <p className="text-[#8A8D91] italic">Personne sur la liste Sparky pour l&apos;instant.</p>
          ) : filtered.waitlist.map((w, i) => (
            <article key={i} className="bg-white border border-[#E8E6E1] rounded-2xl px-4 py-3 flex items-center justify-between gap-4">
              <span className="text-[#3D405B] font-medium truncate">{w.email}</span>
              <span className="text-xs text-[#8A8D91] shrink-0">{new Date(w.created_at).toLocaleString("fr-FR")} · {w.lang || "?"}</span>
            </article>
          ))}
        </section>
      )}

      {tab === "reviews" && (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tab-reviews">
          {(filtered.reviews || []).length === 0 ? (
            <p className="text-[#8A8D91] italic col-span-full">Aucun avis pour l&apos;instant.</p>
          ) : filtered.reviews.map((r, i) => (
            <article
              key={r.id || i}
              className={`border rounded-2xl p-4 transition-colors ${
                r.hidden
                  ? "bg-[#F4F1EA] border-dashed border-[#B9BAC0] opacity-70"
                  : "bg-white border-[#E8E6E1]"
              }`}
              data-testid={`admin-review-${r.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-1.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className="w-4 h-4" fill={n <= r.rating ? "#F4A261" : "none"} stroke={n <= r.rating ? "#F4A261" : "#B9BAC0"} strokeWidth={2} />
                  ))}
                  <span className="ml-1 text-sm font-semibold text-[#3D405B]">{r.rating}/5</span>
                  {r.hidden && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#3D405B] text-white">Masqué</span>
                  )}
                </div>
                <span className="text-[10px] uppercase tracking-widest text-[#8A8D91] shrink-0">
                  {new Date(r.created_at).toLocaleString("fr-FR")} · {r.lang || "?"}
                </span>
              </div>
              <div className="text-sm font-semibold text-[#3D405B] mt-2">{r.name || "Un parent anonyme"}</div>
              <p className="text-sm text-[#3D405B]/85 mt-1 leading-relaxed whitespace-pre-wrap">{r.text}</p>
              <div className="mt-3 flex justify-end">
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API}/admin/reviews/${r.id}/toggle-hidden`, {
                        method: "POST",
                        headers: { "X-Admin-Token": encodeURIComponent(token) },
                      });
                      if (!res.ok) throw new Error("hide failed");
                      const j = await res.json();
                      toast.success(j.hidden ? "Avis masqué" : "Avis réaffiché");
                      // Refresh
                      setData((d) => d && ({
                        ...d,
                        reviews: (d.reviews || []).map((x) => x.id === r.id ? { ...x, hidden: j.hidden } : x),
                      }));
                    } catch {
                      toast.error("Impossible de masquer l'avis");
                    }
                  }}
                  data-testid={`admin-review-toggle-${r.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#3D405B]/20 bg-white text-[#3D405B] px-3 py-1.5 text-xs font-medium hover:bg-[#3D405B]/5"
                >
                  {r.hidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  {r.hidden ? "Réafficher" : "Masquer"}
                </button>
              </div>
            </article>
          ))}
        </section>
      )}
      {showChangePwd && (
        <ChangePasswordModal
          token={token}
          onClose={() => setShowChangePwd(false)}
          onChanged={(newPwd) => {
            localStorage.setItem(STORAGE_KEY, newPwd);
            setToken(newPwd);
            setShowChangePwd(false);
          }}
        />
      )}
    </div>
  );
}
